import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LanOutlinedIcon from "@mui/icons-material/LanOutlined";

import {
  calcularCobrosPuntosRed,
  DEFAULT_PUNTO_RED_METROS_LIBRES,
  DEFAULT_PUNTO_RED_PRECIO_METRO,
} from "./usePreciosConceptosVisita";
import type { ConfigPuntoRed } from "./usePreciosConceptosVisita";
import CampoFotoEvidencia from "../evidencias/CampoFotoEvidencia";
import type { FotoEvidencia } from "../evidencias/CampoFotoEvidencia";

/**
 * "Puntos de Red" de una visita: cada punto captura los METROS de cable UTP
 * usados; los primeros `metros_libres` no se cobran y el excedente se cobra por
 * metro o fracción (ver `calcularCobroPuntoRed`). Requiere que el técnico haya
 * seleccionado CABLE UTP en los Materiales de la visita.
 *
 * Componente CONTROLADO y reutilizable (editor + readOnly). Se persiste en
 * `agendamiento.observaciones.puntos_red` y al finalizar la visita el excedente
 * se envía a Softnet (rubro CABLE UTP).
 */

export type PuntoRed = {
  nombre?: string;
  metros: number;
  /**
   * Concepto (de "Otros conceptos") al que se liga este punto. Si viene, los
   * metros libres del concepto aplican a este punto (junto con los demás puntos
   * del mismo concepto). Si es undefined/"" el punto es LIBRE y se cobra todo.
   */
  concepto?: string;
  // Calculados (snapshot al guardar): metros cobrados (excedente) + precio.
  metros_cobrados?: number;
  valor_sin_iva?: number;
  iva?: number;
  valor_total?: number;
  // Evidencia fotográfica (sube el técnico): punto instalado + paso UTP con grapas.
  foto_punto?: FotoEvidencia | null;
  foto_utp?: FotoEvidencia | null;
};

const ACCENT = "#6a1b9a";

type Props = {
  value?: PuntoRed[] | null;
  onChange?: (value: PuntoRed[]) => void;
  readOnly?: boolean;
  config?: ConfigPuntoRed;
  ivaPorcentaje?: number;
  /** Si false y no readOnly, avisa que falta seleccionar CABLE UTP en materiales. */
  tieneCableUtp?: boolean;
  /**
   * Nombres de los conceptos de la visita que permiten punto de red (catálogo con
   * `permite_punto_red`). Si hay, cada punto puede ligarse a uno de ellos.
   */
  conceptosDisponibles?: string[];
  /** Sube una foto de evidencia (solo técnico); si viene, habilita los campos de foto. */
  onUploadFoto?: (file: File) => Promise<FotoEvidencia>;
  title?: string;
};

const money = (n?: number | null) =>
  n == null ? "—" : `$${Number(n).toFixed(2)}`;

export default function PuntosDeRedVisita({
  value,
  onChange,
  readOnly = false,
  config = {
    precio_metro_sin_iva: DEFAULT_PUNTO_RED_PRECIO_METRO,
    metros_libres: DEFAULT_PUNTO_RED_METROS_LIBRES,
  },
  ivaPorcentaje = 15,
  tieneCableUtp = true,
  conceptosDisponibles = [],
  onUploadFoto,
  title = "Puntos de Red",
}: Props) {
  const items: PuntoRed[] = Array.isArray(value) ? value : [];

  // Recalcula TODOS los puntos con el excedente GLOBAL (los 15m libres se aplican
  // al total sumado, waterfall en orden). Se emite el array con los cálculos.
  const emit = (next: PuntoRed[]) => {
    if (!onChange) return;
    const cobros = calcularCobrosPuntosRed(next, config, ivaPorcentaje);
    onChange(
      next.map((p, i) => ({
        ...p,
        metros_cobrados: cobros[i]?.metros_cobrados ?? 0,
        valor_sin_iva: cobros[i]?.valor_sin_iva ?? 0,
        iva: cobros[i]?.iva ?? 0,
        valor_total: cobros[i]?.valor_total ?? 0,
      }))
    );
  };

  const addPunto = () =>
    emit([...items, { nombre: `Punto de red ${items.length + 1}`, metros: 0 }]);

  const removePunto = (idx: number) => emit(items.filter((_, i) => i !== idx));

  const updatePunto = (idx: number, patch: Partial<PuntoRed>) =>
    emit(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const totalMetros = items.reduce((s, it) => s + (Number(it.metros) || 0), 0);
  const totalCobro = items.reduce((s, it) => s + (Number(it.valor_total) || 0), 0);
  const metrosLibres = Number(config.metros_libres) || 0;
  const metrosCobrados = items.reduce((s, it) => s + (Number(it.metros_cobrados) || 0), 0);

  // ---------------- READ-ONLY ----------------
  if (readOnly) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <LanOutlinedIcon fontSize="small" sx={{ color: ACCENT }} />
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
        </Stack>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Sin puntos de red registrados.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {items.map((it, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                <Stack direction="row" alignItems="center" gap={1.5} sx={{ flexWrap: "wrap" }}>
                  <Typography variant="body2" fontWeight={600} sx={{ minWidth: 130 }}>
                    {it.nombre || `Punto ${i + 1}`}
                  </Typography>
                  {it.concepto ? (
                    <Chip size="small" color="info" variant="outlined" label={`Concepto: ${it.concepto}`} />
                  ) : (
                    <Chip size="small" color="warning" variant="outlined" label="Sin concepto (cobra todo)" />
                  )}
                  <Chip size="small" label={`${Number(it.metros) || 0} m`} variant="outlined" />
                  {(it.metros_cobrados ?? 0) > 0 ? (
                    <Chip size="small" label={`${it.metros_cobrados} m cobrados`} variant="outlined" />
                  ) : (
                    <Chip size="small" label="Sin excedente" variant="outlined" color="success" />
                  )}
                  <Typography variant="body2" fontWeight={700} sx={{ color: ACCENT }}>
                    {money(it.valor_total)}
                  </Typography>
                </Stack>
                {(it.foto_punto?.url || it.foto_utp?.url) && (
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                      <CampoFotoEvidencia label="Punto de red instalado" value={it.foto_punto} readOnly />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CampoFotoEvidencia label="Paso del UTP con grapas" value={it.foto_utp} readOnly />
                    </Grid>
                  </Grid>
                )}
              </Paper>
            ))}
            <Typography variant="caption" color="text.secondary">
              Total puntos de red: <b>{money(totalCobro)}</b>
            </Typography>
          </Stack>
        )}
      </Box>
    );
  }

  // ---------------- EDITOR ----------------
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <LanOutlinedIcon fontSize="small" sx={{ color: ACCENT }} />
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
      </Stack>

      {!tieneCableUtp && (
        <Alert severity={items.length > 0 ? "error" : "warning"} sx={{ mb: 1.5 }}>
          {items.length > 0 ? (
            <>
              Hay <b>{items.length}</b> punto(s) de red registrados pero falta el <b>CABLE UTP</b> en
              los Materiales de la visita. Vuelve a agregar el <b>CABLE UTP (PDR.017…)</b>; no podrás
              finalizar la visita hasta hacerlo.
            </>
          ) : (
            <>
              Para agregar puntos de red primero selecciona <b>CABLE UTP</b> en los Materiales de la
              visita.
            </>
          )}
        </Alert>
      )}

      <Stack spacing={1.5}>
        {items.map((it, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "rgba(106,27,154,0.03)" }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Punto de red"
                  value={it.nombre || ""}
                  onChange={(e) => updatePunto(i, { nombre: e.target.value })}
                  placeholder="Ubicación / referencia"
                />
              </Grid>
              <Grid item xs={8} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Metros de cable UTP"
                  inputProps={{ min: 0, step: 0.1 }}
                  value={it.metros ?? 0}
                  onChange={(e) => updatePunto(i, { metros: Number(e.target.value) })}
                />
              </Grid>
              <Grid item xs={4} sm={3} sx={{ textAlign: "right" }}>
                <Tooltip title="Quitar punto de red">
                  <IconButton size="small" color="error" onClick={() => removePunto(i)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
              {conceptosDisponibles.length > 0 && (
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Concepto asociado"
                    value={it.concepto || ""}
                    onChange={(e) => updatePunto(i, { concepto: e.target.value || undefined })}
                    helperText={
                      it.concepto
                        ? `Los primeros ${metrosLibres} m de "${it.concepto}" no se cobran (se suman los puntos de ese concepto).`
                        : "Sin concepto: se cobran todos los metros de este punto."
                    }
                  >
                    <MenuItem value="">Sin concepto (se cobra todo)</MenuItem>
                    {conceptosDisponibles.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, ml: 0.5 }}>
              {(it.metros_cobrados ?? 0) > 0 ? (
                <>
                  Cobra <b>{it.metros_cobrados} m</b> · <b>{money(it.valor_total)}</b> (con IVA)
                </>
              ) : (
                <>Sin cobro (cubierto por los metros libres)</>
              )}
            </Typography>
            {/* Evidencias del punto de red — 2 columnas fijas (no se reacomodan
                al subir la foto) */}
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <CampoFotoEvidencia
                  label="Punto de red instalado"
                  value={it.foto_punto}
                  onUpload={onUploadFoto}
                  onChange={(f) => updatePunto(i, { foto_punto: f })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CampoFotoEvidencia
                  label="Paso del UTP con grapas"
                  value={it.foto_utp}
                  onUpload={onUploadFoto}
                  onChange={(f) => updatePunto(i, { foto_utp: f })}
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Stack>

      <Button
        startIcon={<AddIcon />}
        variant="outlined"
        size="small"
        onClick={addPunto}
        disabled={!tieneCableUtp}
        sx={{ mt: 1.5 }}
      >
        Agregar Punto de red
      </Button>

      {items.length > 0 && (
        <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1.5, bgcolor: "rgba(106,27,154,0.06)" }}>
          <Typography variant="body2">
            Total metros: <b>{totalMetros} m</b> · Libres: <b>{metrosLibres} m</b> · Cobrados:{" "}
            <b>{metrosCobrados} m</b>
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: ACCENT }}>
            Total a cobrar (con IVA): {money(totalCobro)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
